import { BaseComponent, html, nothing } from '../../lib/lit.js';
import { router } from '../../lib/router.js';
import { FEEDBACK_TYPES } from './community-give-feedback.js';

const TYPE_BY_VALUE = Object.fromEntries(FEEDBACK_TYPES.map((t) => [t.value, t]));

/** @param {string} iso */
export function formatItemDate(iso) {
    if (!iso) return '';
    const date = new Date(iso);
    return Number.isNaN(date.getTime())
        ? ''
        : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** The icon and label for a stored type value. */
export function itemTypeMeta(value) {
    return TYPE_BY_VALUE[value] ?? { icon: 'bi-dot', label: value };
}

/**
 * <community-item-list> — a list of feedback items, each linking to its page.
 *
 * Serves both dashboard regions stage 1 can honestly fill: "your activity" and
 * community-wide recent activity. They differ only in heading and whether the
 * author is worth showing (on your own list it is always you), so they are one
 * component rather than two that drift.
 *
 * The `actions` slot carries a heading-row button.
 */
export class CommunityItemList extends BaseComponent {
    static properties = {
        items: { type: Array },
        flush: { type: Boolean },
        heading: { type: String },
        showAuthor: { type: Boolean },
        empty: { type: String },
    };

    constructor() {
        super();
        /** @type {Array<object>} */
        this.items = [];
        this.flush = false;
        this.heading = '';
        this.showAuthor = false;
        this.empty = '';
    }

    _open(id) {
        router.navigate(`/items/${id}`);
    }

    _renderItem(item) {
        const type = itemTypeMeta(item.type);
        const closed = item.state === 'closed';

        return html`
            <button
                type="button"
                class="list-group-item list-group-item-action text-start"
                @click=${() => this._open(item.id)}
            >
                <div class="d-flex align-items-start">
                    <i class="bi ${type.icon} me-2 mt-1 text-secondary" title=${type.label}></i>
                    <div class="flex-grow-1 min-w-0">
                        <div class="d-flex align-items-center gap-2 flex-wrap">
                            <span class="fw-semibold">${item.title}</span>
                            ${
                                closed
                                    ? html`<span class="badge text-bg-secondary">Closed</span>`
                                    : html`<span class="badge text-bg-success">Open</span>`
                            }
                        </div>
                        <div class="text-muted small">
                            ${type.label} ·
                            ${
                                this.showAuthor && item.authorHandle
                                    ? html`${item.authorHandle} · `
                                    : nothing
                            }${formatItemDate(item.createdAt)}
                        </div>
                    </div>
                    <i class="bi bi-chevron-right text-secondary ms-2 mt-1"></i>
                </div>
            </button>
        `;
    }

    render() {
        return html`
            ${
                this.heading
                    ? html`<div class="d-flex align-items-center justify-content-between mb-3">
                          <h2 class="h5 mb-0">${this.heading}</h2>
                          <slot name="actions"></slot>
                      </div>`
                    : nothing
            }
            ${
                this.items.length === 0
                    ? html`<div class="text-muted ${this.flush ? 'p-3' : 'mb-4'}">${this.empty}</div>`
                    : html`<div
                          class="list-group ${this.flush ? 'list-group-flush' : 'shadow-sm mb-4'}"
                      >
                          ${this.items.map((item) => this._renderItem(item))}
                      </div>`
            }
        `;
    }
}

customElements.define('community-item-list', CommunityItemList);
